import { FULL_DATE } from '@/constants/dateFormat';
import dayjs from 'dayjs';
import * as Yup from 'yup';

/**
 * 上传品牌item
 */
export interface ReceiptsItemInfo {
  /**
   * 上传品牌名称
   */
  brand: string;
  /**
   * 金额
   */
  price: string;
}

/**
 * 上传小票
 */
export interface UploadInfo {
  /**
   * 手机号
   */
  phone?: string,
  /**
   * 会员号
   */
  user_code?: string,
  /**
   * 会员客户id(只有会员可以上传小票)
   */
  member_id?: number,
  /**
   * 消费日期
   */
  consume_time: string,
  /**
   * DRT销售id
   */
  pcs_id?: number,
  /**
   * 销售渠道
   */
  sales_source?: number,
  /**
   * 提交时转json字符串 里面包含以下数据 上传品牌,金额
   */
  receipts: ReceiptsItemInfo[];
}

/**
 * 历史小票记录查询表单
 */
export interface RecordInfo {
  /**
   * 小票记录所属购物村
   */
  shopping_village: number,
  /**
   * 品牌
   */
  brand_id: number,
  /**
   * 手机号
   */
  phone: string,
  /**
   * 会员号
   */
  user_code: string
  /**
   * 消费日期
   * - start_at 开始时间
   * - end_at 结束时间
   */
  time_range: string[],
}

/**
 * 上传小票表单默认值
 */
export const defaultUploadInfo: UploadInfo = {
  phone: '',
  user_code: '',
  member_id: null,
  consume_time: dayjs(new Date()).format(FULL_DATE),
  pcs_id: null,
  sales_source: null,
  receipts: [
    {
      brand: '',
      price: '',
    }
  ],
};

/**
 * 历史小票记录查询表单默认值
 */
export const defaultRecordInfo: RecordInfo = {
  shopping_village: null,
  brand_id: null,
  phone: '',
  user_code: '',
  time_range: [],
};

/**
 * 上传小票表单校验
 */
export const uploadReceiptSchema = Yup.object().shape(
  {
    phone: Yup.string().when(['user_code', 'member_id'], {
      is: (user_code, member_id) => !user_code && !member_id,
      then: Yup.string().required('请输入手机号或会员号匹配会员信息'),
    }).test('memberId', '请输入手机号或会员号匹配会员信息', function (this, value) {
      if (value && !this.parent.member_id) {
        return this.createError({ message: '请点击搜索按钮匹配会员信息' });
      }
      return true;
    }),
    user_code: Yup.string().when(['phone', 'member_id'], {
      is: (phone, member_id) => !phone && !member_id,
      then: Yup.string().required('请输入手机号或会员号匹配会员信息'),
    }).test('memberId', '请输入手机号或会员号匹配会员信息', function (this, value) {
      if (value && !this.parent.member_id) {
        return this.createError({ message: '请点击搜索按钮匹配会员信息' });
      }
      return true;
    }),
    consume_time: Yup.string().ensure().required('请选择消费日期'),
    receipts: Yup.array()
      .of(
        Yup.object({
          brand: Yup.string().required('请选择上传品牌'),
          price: Yup.string().required('请输入金额').matches(/^((([1-9]|[1-9]\d{1,5})(?:([.]{0,1})|([.]{1}\d{1,2})))|(0[.]{1}\d{1,2})|1000000|1000000.0|1000000.00)$/, '请输入0-1000000至多两位小数的非零金额'),
        })
      )
      .min(1, '请输入上传品牌信息')
      .required('请输入上传品牌信息'),
  },
  [['phone', 'user_code']]
);

/**
 * 上传小票表单校验-DRT角色
 */
export const uploadReceiptDRTSchema = Yup.object().shape(
  {
    phone: Yup.string().when(['user_code', 'member_id'], {
      is: (user_code, member_id) => !user_code && !member_id,
      then: Yup.string().required('请输入手机号或会员号匹配会员信息'),
    }).test('memberId', '请输入手机号或会员号匹配会员信息', function (this, value) {
      if (value && !this.parent.member_id) {
        return this.createError({ message: '请点击搜索按钮匹配会员信息' });
      }
      return true;
    }),
    user_code: Yup.string().when(['phone', 'member_id'], {
      is: (phone, member_id) => !phone && !member_id,
      then: Yup.string().required('请输入手机号或会员号匹配会员信息'),
    }).test('memberId', '请输入手机号或会员号匹配会员信息', function (this, value) {
      if (value && !this.parent.member_id) {
        return this.createError({ message: '请点击搜索按钮匹配会员信息' });
      }
      return true;
    }),
    consume_time: Yup.string().ensure().required('请选择消费日期'),
    pcs_id: Yup.number().typeError('请选择DRT销售').required('请选择DRT销售'),
    sales_source: Yup.number().typeError('请选择销售渠道').required('请选择销售渠道'),
    receipts: Yup.array()
      .of(
        Yup.object({
          brand: Yup.string().required('请选择上传品牌'),
          price: Yup.string().required('请输入金额').matches(/^[1-9]*[1-9][0-9]*$/, '请输入正整数金额').matches(/^([1-9]|[1-9]\d{1,5}|1000000)$/, '请输入1-1000000的正确金额'),
        })
      )
      .min(1, '请输入上传品牌信息')
      .required('请输入上传品牌信息'),
  },
  [['phone', 'user_code']]
);